import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMainStore } from "@/stores";
import CommonAvatar from "../../ui/Avatar/CommonAvatar";
import { LogOut, Settings, ChevronDown, ChevronUp } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@heroui/react";
import LogoutConfirm from "../../ui/Logout/LogoutConfirm";

const SidebarAvatar = ({ expanded }) => {
  const navigate = useNavigate();
  const { user } = useMainStore();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const fullName = user?.name || user?.email || "User";
  const plan = user?.plan || "Free";

  const handleSettings = () => {
    setIsMenuOpen(false);
    setIsPopoverOpen(false);
    navigate("/settings/profile_settings");
  };

  const handleLogout = () => {
    setIsMenuOpen(false);
    setIsPopoverOpen(false);
    setShowLogoutConfirm(true);
  };

  if (!expanded) {
    return (
      <>
        <Popover
          isOpen={isPopoverOpen}
          onOpenChange={setIsPopoverOpen}
          placement="right-end"
          offset={12}
        >
          <PopoverTrigger>
            <button
              type="button"
              aria-label="Open account menu"
              className="relative flex items-center justify-center w-10 h-10 mx-auto cursor-pointer rounded-full outline-none focus-visible:ring-2 focus-visible:ring-secondary/40"
            >
              <CommonAvatar
                imageUrl={user?.profileImage}
                name={user?.name}
                className="w-8 h-8 rounded-full object-cover shrink-0"
              />
              <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
            </button>
          </PopoverTrigger>

          <PopoverContent className="w-56 rounded-xl border border-gray-100 dark:border-gray-800 p-3 shadow-lg bg-white dark:bg-gray-900">
            <div className="flex flex-col gap-2 w-full">
              <div className="flex items-center gap-2.5 border-b border-gray-100 dark:border-gray-800 pb-2.5">
                <CommonAvatar
                  imageUrl={user?.profileImage}
                  name={user?.name}
                  className="h-8 w-8 rounded-full object-cover shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-gray-900 dark:text-gray-100" title={fullName}>
                    {fullName}
                  </p>
                  <p className="truncate text-[10px] text-gray-500 dark:text-gray-400">
                    {user?.email}
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-0.5 pt-0.5">
                <button
                  type="button"
                  onClick={handleSettings}
                  className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium text-gray-700 dark:text-gray-300 transition-colors hover:bg-gray-100/80 dark:hover:bg-gray-800 cursor-pointer"
                >
                  <Settings size={15} className="text-gray-500 dark:text-gray-400" />
                  <span>Settings</span>
                </button>

                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-xs font-medium text-gray-700 dark:text-gray-300 transition-colors hover:bg-red-50 dark:hover:bg-red-950/30 hover:text-red-600 dark:hover:text-red-400 cursor-pointer group"
                >
                  <LogOut size={15} className="text-gray-500 dark:text-gray-400 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors" />
                  <span>Log out</span>
                </button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {showLogoutConfirm && (
          <LogoutConfirm
            showLogoutConfirm={showLogoutConfirm}
            onClose={() => setShowLogoutConfirm(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="flex flex-col w-full rounded-xl border border-gray-200/90 dark:border-gray-800 bg-white dark:bg-gray-900/90 p-3 font-sans shadow-2xs hover:border-gray-300/80 dark:hover:border-gray-700 transition-colors">
        {/* Profile Card Header (Clickable to toggle menu) */}
        <div
          onClick={() => setIsMenuOpen((prev) => !prev)}
          className="flex items-start gap-2.5 min-w-0 cursor-pointer select-none group"
        >
          <div className="relative shrink-0 mt-0.5">
            <CommonAvatar
              imageUrl={user?.profileImage}
              name={user?.name}
              className="w-9 h-9 rounded-full object-cover shrink-0"
            />
            <span className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-gray-900" />
          </div>

          <div className="flex flex-col min-w-0 flex-1 gap-0.5">
            {/* Row 1: Full Name + Chevron toggle */}
            <div className="flex items-center justify-between gap-1 min-w-0">
              <h4
                title={fullName}
                className="text-[13px] font-semibold text-gray-900 dark:text-gray-100 truncate leading-tight group-hover:text-secondary dark:group-hover:text-secondary-light transition-colors"
              >
                {fullName}
              </h4>
              <div className="shrink-0 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors">
                {isMenuOpen ? (
                  <ChevronUp size={14} />
                ) : (
                  <ChevronDown size={14} />
                )}
              </div>
            </div>

            {/* Row 2: Email */}
            <p
              title={user?.email}
              className="text-[11px] font-normal text-gray-500 dark:text-gray-400 truncate leading-snug tracking-tight"
            >
              {user?.email}
            </p>

            {/* Row 3: Subscription plan badge below email */}
            <div className="mt-1 flex items-center">
              <span className="inline-flex items-center rounded-md bg-secondary/10 px-2 py-0.5 text-[10px] font-semibold capitalize text-secondary dark:text-secondary-light leading-none border border-secondary/20 tracking-wide">
                {plan}
              </span>
            </div>
          </div>
        </div>

        {/* Dropdown Section: Log out */}
        {isMenuOpen && (
          <div className="mt-2.5 pt-2 border-t border-gray-100/90 dark:border-gray-800 flex flex-col gap-0.5 animate-in fade-in slide-in-from-top-1 duration-150">
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-2.5 w-full px-2.5 py-1.5 rounded-lg text-left hover:bg-red-50/80 dark:hover:bg-red-950/30 cursor-pointer transition group"
            >
              <LogOut size={14} className="text-gray-500 dark:text-gray-400 group-hover:text-red-600 dark:group-hover:text-red-400 shrink-0 transition-colors" />
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
                Log out
              </span>
            </button>
          </div>
        )}
      </div>

      {showLogoutConfirm && (
        <LogoutConfirm
          showLogoutConfirm={showLogoutConfirm}
          onClose={() => setShowLogoutConfirm(false)}
        />
      )}
    </>
  );
};

export default SidebarAvatar;
